/**
 * Undoes the backslash doubling that our exercise content was authored with.
 *
 * Before ngx-markdown 22 KaTeX ran on the *rendered* DOM, so every `$…$` had
 * already been through marked's escape pass and `\\` had collapsed to `\`.
 * Staff therefore doubled every backslash markdown would otherwise eat —
 * `\\;`, `\\{`, `\\}`, `\\cdot`, and `\\\` for a row break. ngx-markdown 22
 * tokenizes math *before* escaping and hands the raw source to KaTeX, where
 * `\\;` reads as "row break, then a semicolon": a table row opens in the wrong
 * place and `\hline` fails with "valid only within array environment".
 *
 * So we collapse the doubling ourselves, on math tokens only, and only where a
 * row break cannot have been meant:
 *   - three or more backslashes are always a double-escaped row break;
 *   - `\\` before a command word (`\\cdot`) or before punctuation markdown used
 *     to eat (`\\;`, `\\{`) is a double-escaped control sequence;
 *   - anything else — `\\` before a space, a digit, `[` — stays a row break.
 * Single backslashes are never touched, so content written after the upgrade
 * (`\;`, `\{`, `x \\ y` inside an array) keeps rendering as its author saw it.
 *
 * Checked against the whole exercise corpus (20.6k math segments): 135 KaTeX
 * errors and 914 silently mis-rendered segments repaired, nothing regressed.
 *
 * Kept byte-identical with clients/main's copy of this file — the exercise
 * editor must preview exactly what the student ends up seeing.
 */

/** The punctuation marked's escape rule used to swallow — minus `[`, which is
 *  the start of a genuine row-break option (`\\[4pt]`). */
const LEGACY_ESCAPED_PUNCTUATION = /[!"#$%&'()*+,\-./:;<=>?@\]^_`{|}~]/;

/** Collapses legacy double-escaped backslashes in a single KaTeX expression. */
export function unescapeLegacyMath(math: string): string {
    return math.replace(/\\{2,}/g, (run, index: number) => {
        if (run.length > 2) return '\\\\';

        const rest = math.slice(index + run.length);
        const isCommand = /^[a-zA-Z]{2,}/.test(rest);
        const isPunctuation = LEGACY_ESCAPED_PUNCTUATION.test(rest.charAt(0));

        return isCommand || isPunctuation ? '\\' : run;
    });
}

/** marked extension applying {@link unescapeLegacyMath} to math tokens only —
 *  prose keeps its own escaping rules. Registered via `MARKED_EXTENSIONS`. */
export const legacyMathEscapesExtension = {
    walkTokens(token: { type: string; text?: string }): void {
        if (token.type !== 'inlineKatex' && token.type !== 'blockKatex') return;
        if (token.text) token.text = unescapeLegacyMath(token.text);
    },
};
